import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const api = axios.create({
  baseURL: API_URL,
  timeout: 60000,
  maxBodyLength: Infinity,
  maxContentLength: Infinity,
});

// Intercepteur pour ajouter le token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('access_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Intercepteur pour gérer les erreurs avec refresh automatique
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    
    // Si ce n'est pas une erreur 401, rejeter directement
    if (error.response?.status !== 401) {
      return Promise.reject(error);
    }
    
    // Si c'est une requête de refresh qui échoue, NE PAS déconnecter
    // Juste rejeter l'erreur et laisser l'utilisateur décider
    if (originalRequest.url?.includes('/auth/refresh')) {
      console.warn('Refresh token failed, mais session conservée');
      return Promise.reject(error);
    }
    
    // Si on est déjà en train de rafraîchir, mettre la requête en attente
    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        failedQueue.push({ resolve, reject });
      }).then(token => {
        originalRequest.headers.Authorization = `Bearer ${token}`;
        return api(originalRequest);
      }).catch(err => Promise.reject(err));
    }
    
    // Marquer qu'on est en train de rafraîchir
    originalRequest._retry = true;
    isRefreshing = true;
    
    const refreshToken = localStorage.getItem('refresh_token');
    
    if (!refreshToken) {
      isRefreshing = false;
      // Pas de refresh token = ne pas déconnecter, juste rejeter l'erreur
      return Promise.reject(error);
    }
    
    try {
      const response = await axios.post(`${API_URL}/api/auth/refresh`, {
        refresh_token: refreshToken
      });
      
      const { access_token, refresh_token: newRefreshToken } = response.data;
      
      localStorage.setItem('access_token', access_token);
      if (newRefreshToken) {
        localStorage.setItem('refresh_token', newRefreshToken);
      }
      
      // Mettre à jour le header pour la requête originale
      originalRequest.headers.Authorization = `Bearer ${access_token}`;
      
      // Traiter les requêtes en attente
      processQueue(null, access_token);
      
      isRefreshing = false;
      
      // Réexécuter la requête originale
      return api(originalRequest);
    } catch (refreshError) {
      processQueue(refreshError, null);
      isRefreshing = false;
      // Ne PAS déconnecter automatiquement - laisser l'utilisateur décider
      console.warn('Token refresh failed, keeping session');
      return Promise.reject(error);
    }
  }
);

// Auth
export const auth = {
  login: (email, password) => api.post('/api/auth/login', { email, password }),
  me: () => api.get('/api/auth/me'),
  switchOrganization: (orgId) => api.put(`/api/auth/switch-organization/${orgId}`),
};

// Organizations (Golfs)
export const orgs = {
  list: () => api.get('/api/organizations'),
};

// Admin - Gestion des utilisateurs
export const adminUsers = {
  list: () => api.get('/api/admin/users'),
  create: (data) => api.post('/api/admin/users', data),
  update: (id, data) => api.put(`/api/admin/users/${id}`, data),
  delete: (id) => api.delete(`/api/admin/users/${id}`),
};

export const permissionTemplates = {
  list: () => api.get('/api/admin/permission-templates'),
  create: (data) => api.post('/api/admin/permission-templates', data),
  update: (id, data) => api.put(`/api/admin/permission-templates/${id}`, data),
  delete: (id) => api.delete(`/api/admin/permission-templates/${id}`),
};

// Zones
export const zones = {
  list: () => api.get('/api/zones'),
  get: (id) => api.get(`/api/zones/${id}`),
  create: (data) => api.post('/api/zones', data),
  update: (id, data) => api.put(`/api/zones/${id}`, data),
  delete: (id) => api.delete(`/api/zones/${id}`),
};

// Produits
export const products = {
  list: () => api.get('/api/products'),
  get: (id) => api.get(`/api/products/${id}`),
  create: (data) => api.post('/api/products', data),
  update: (id, data) => api.put(`/api/products/${id}`, data),
  delete: (id) => api.delete(`/api/products/${id}`),
};

// Stock
export const stock = {
  current: () => api.get('/api/stock/current'),
  movements: (productId) => api.get('/api/stock/movements', { params: { product_id: productId } }),
  addMovement: (data) => api.post('/api/stock/movements', data),
  addDirect: (data) => api.post('/api/stock/add-direct', data),
  delete: (id) => api.delete(`/api/stock/current/${id}`),
  importExcelAI: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/api/stock/import-excel-ai', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  parseExcelAI: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/api/stock/import-excel-ai/parse', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  confirmExcelAI: (data) => api.post('/api/stock/import-excel-ai/confirm', data),
  mergeCategories: (source, target) => api.post('/api/stock/merge-categories', { source, target }),
};

// Applications
export const applications = {
  list: (params) => api.get('/api/applications', { params }),
  create: (data) => api.post('/api/applications', data),
  update: (id, data) => api.put(`/api/applications/${id}`, data),
  delete: (id) => api.delete(`/api/applications/${id}`),
};

// Employés
export const employees = {
  list: () => api.get('/api/employees'),
  get: (id) => api.get(`/api/employees/${id}`),
  create: (data) => api.post('/api/employees', data),
  update: (id, data) => api.put(`/api/employees/${id}`, data),
  delete: (id) => api.delete(`/api/employees/${id}`),
  // Solde d'heures
  getHourBalance: (id) => api.get(`/api/employees/${id}/hour-balance`),
  getWeeklyHistory: (id, limit = 12) => api.get(`/api/employees/${id}/weekly-history`, { params: { limit } }),
  closeWeek: (id, weekData) => api.post(`/api/employees/${id}/close-week`, weekData),
  adjustBalance: (id, data) => api.post(`/api/employees/${id}/adjust-balance`, data),
};

// Affectations
export const assignments = {
  list: (params) => api.get('/api/assignments', { params }),
  create: (data) => api.post('/api/assignments', data),
  update: (id, data) => api.put(`/api/assignments/${id}`, data),
  delete: (id) => api.delete(`/api/assignments/${id}`),
};

// Barèmes de temps
export const timeStandards = {
  list: () => api.get('/api/time-standards'),
  create: (data) => api.post('/api/time-standards', data),
  update: (id, data) => api.put(`/api/time-standards/${id}`, data),
  delete: (id) => api.delete(`/api/time-standards/${id}`),
};

// Opérations
export const operations = {
  list: (status) => api.get('/api/operations', { params: { status_filter: status } }),
  create: (data) => api.post('/api/operations', data),
  update: (id, data) => api.put(`/api/operations/${id}`, data),
  delete: (id) => api.delete(`/api/operations/${id}`),
};

// Eau
export const water = {
  list: (params) => api.get('/api/water', { params }),
  create: (data) => api.post('/api/water', data),
  update: (id, data) => api.put(`/api/water/${id}`, data),
  delete: (id) => api.delete(`/api/water/${id}`),
};

// Machines
export const machines = {
  list: () => api.get('/api/machines'),
  listSold: () => api.get('/api/machines/sold'),
  get: (id) => api.get(`/api/machines/${id}`),
  create: (data) => api.post('/api/machines', data),
  update: (id, data) => api.put(`/api/machines/${id}`, data),
  delete: (id) => api.delete(`/api/machines/${id}`),
  sell: (id, data) => api.post(`/api/machines/${id}/sell`, data),
};

// Interventions
export const interventions = {
  list: (machineId) => api.get('/api/interventions', { params: { machine_id: machineId } }),
  create: (data) => api.post('/api/interventions', data),
  createWithFile: (formData) => api.post('/api/interventions/with-file', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  update: (id, data) => api.put(`/api/interventions/${id}`, data),
  updateWithFile: (id, formData) => api.put(`/api/interventions/${id}/with-file`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  delete: (id) => api.delete(`/api/interventions/${id}`),
};

// Journal agronomique
export const agronomic = {
  list: (zoneId) => api.get('/api/agronomic-entries', { params: { zone_id: zoneId } }),
  create: (data) => api.post('/api/agronomic-entries', data),
  update: (id, data) => api.put(`/api/agronomic-entries/${id}`, data),
  delete: (id) => api.delete(`/api/agronomic-entries/${id}`),
};

// Incidents
export const incidents = {
  list: (params) => api.get('/api/incidents', { params }),
  create: (data) => api.post('/api/incidents', data),
  update: (id, data) => api.put(`/api/incidents/${id}`, data),
};

// Budget
export const budget = {
  categories: () => api.get('/api/budget/categories'),
  createCategory: (data) => api.post('/api/budget/categories', data),
  list: (fiscalYear) => api.get('/api/budget', { params: { fiscal_year: fiscalYear } }),
  create: (data) => api.post('/api/budget', data),
  update: (id, data) => api.put(`/api/budget/${id}`, data),
};

// Dépenses (Expenses)
export const expenses = {
  list: (category) => api.get('/api/expenses', { params: { category } }),
  create: (formData) => api.post('/api/expenses', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  update: (id, formData) => api.put(`/api/expenses/${id}`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  delete: (id) => api.delete(`/api/expenses/${id}`),
};

// Catalogue de produits (pour les dépenses)
export const catalogProducts = {
  list: (category) => api.get('/api/catalog-products', { params: { category } }),
  get: (id) => api.get(`/api/catalog-products/${id}`),
  create: (data) => api.post('/api/catalog-products', data),
  update: (id, data) => api.put(`/api/catalog-products/${id}`, data),
  delete: (id) => api.delete(`/api/catalog-products/${id}`),
  importExcel: (formData) => api.post('/api/catalog-products/import-excel', formData, { 
    headers: { 'Content-Type': 'multipart/form-data' } 
  }),
  parseInvoice: (formData) => api.post('/api/catalog-products/import-invoice/parse', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  confirmInvoice: (data) => api.post('/api/catalog-products/import-invoice/confirm', data),
};

// Dashboard
export const dashboard = {
  stats: () => api.get('/api/dashboard/stats'),
};

// Absences
export const absencesApi = {
  list: (params) => api.get('/api/absences', { params }),
  create: (data) => api.post('/api/absences', data),
  update: (id, data) => api.put(`/api/absences/${id}`, data),
  delete: (id) => api.delete(`/api/absences/${id}`),
};

// Compétitions
export const competitionsApi = {
  list: (params) => api.get('/api/competitions', { params }),
  create: (data) => api.post('/api/competitions', data),
  update: (id, data) => api.put(`/api/competitions/${id}`, data),
  delete: (id) => api.delete(`/api/competitions/${id}`),
  // Admin - gestion des inscriptions
  adminRegister: (competitionId, data) => api.post(`/api/competitions/${competitionId}/admin/register`, data),
  adminRefundOptions: (competitionId, registrationId) => api.get(`/api/competitions/${competitionId}/admin/register/${registrationId}/refund-options`),
  adminCancelRegistration: (competitionId, registrationId, refundMode = 'none') => api.delete(`/api/competitions/${competitionId}/admin/register/${registrationId}`, { params: { refund_mode: refundMode } }),
};

// Stages
export const stagesApi = {
  list: (params) => api.get('/api/stages', { params }),
  create: (data) => api.post('/api/stages', data),
  update: (id, data) => api.put(`/api/stages/${id}`, data),
  delete: (id) => api.delete(`/api/stages/${id}`),
  // Admin - gestion des inscriptions
  adminRegister: (stageId, data) => api.post(`/api/stages/${stageId}/admin/register`, data),
  adminRefundOptions: (stageId, registrationId) => api.get(`/api/stages/${stageId}/admin/register/${registrationId}/refund-options`),
  adminCancelRegistration: (stageId, registrationId, refundMode = 'none') => api.delete(`/api/stages/${stageId}/admin/register/${registrationId}`, { params: { refund_mode: refundMode } }),
};

// Exports
export const exports = {
  zonesCSV: () => api.get('/api/export/zones/csv', { responseType: 'blob' }),
  stockCSV: () => api.get('/api/export/stock/csv', { responseType: 'blob' }),
  zonesExcel: () => api.get('/api/export/zones/excel', { responseType: 'blob' }),
  stockExcel: () => api.get('/api/export/stock/excel', { responseType: 'blob' }),
  catalogExcel: () => api.get('/api/export/catalog/excel', { responseType: 'blob' }),
  employeesExcel: () => api.get('/api/export/employees/excel', { responseType: 'blob' }),
  machinesExcel: () => api.get('/api/export/machines/excel', { responseType: 'blob' }),
};

// Import avec IA
export const importApi = {
  // Import classique
  tasks: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/api/import/tasks', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  machines: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/api/import/machines', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  competitions: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/api/import/competitions', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  operations: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/api/import/operations', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  employees: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/api/import/employees', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  zones: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/api/import/zones', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  products: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/api/import/products', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  
  // Import avec IA
  aiOperations: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/api/import/ai/operations', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  aiInvoice: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/api/import/ai/invoice', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  aiPricingGrid: (files) => {
    const formData = new FormData();
    files.forEach(file => formData.append('files', file));
    return api.post('/api/import/ai/pricing-grid', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  aiGeneric: (type, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/api/import/ai/generic/${type}`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  
  getTemplate: (type) => api.get(`/api/import/templates/${type}`, { responseType: 'blob' }),
};

// Alex - Agent IA Agronomique et Mécanique
export const alex = {
  // Météo et recommandations
  getWeather: () => api.get('/api/alex/weather'),
  getRecommendations: () => api.get('/api/alex/recommendations'),
  
  // Chat texte
  sendMessage: (message, conversationId = null) => {
    const formData = new FormData();
    formData.append('message', message);
    if (conversationId) {
      formData.append('conversation_id', conversationId);
    }
    return api.post('/api/alex/chat', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  
  // Chat avec image (diagnostic mécanique)
  sendMessageWithImage: (message, image, conversationId = null) => {
    const formData = new FormData();
    formData.append('message', message || '');
    formData.append('image', image);
    if (conversationId) {
      formData.append('conversation_id', conversationId);
    }
    return api.post('/api/alex/chat-with-image', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  
  getConversations: () => api.get('/api/alex/conversations'),
  getConversation: (id) => api.get(`/api/alex/conversations/${id}`),
  
  // Documents
  uploadDocument: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/api/alex/documents', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  getDocuments: () => api.get('/api/alex/documents'),
  deleteDocument: (id) => api.delete(`/api/alex/documents/${id}`),
  
  // Configuration
  getConfig: () => api.get('/api/alex/config'),
  updateConfig: (config) => api.put('/api/alex/config', config),
  
  // Bilan hebdomadaire
  getWeeklyReport: () => api.get('/api/alex/weekly-report'),
  generateWeeklyReport: () => api.post('/api/alex/weekly-report/generate'),
  getWeeklyReports: () => api.get('/api/alex/weekly-reports'),
  sendWeeklyReportEmail: () => api.post('/api/alex/weekly-report/send'),
  getReportRecipients: () => api.get('/api/alex/report-recipients'),
  updateReportRecipients: (recipients) => api.put('/api/alex/report-recipients', { recipients }),
  
  // Actions
  executeAction: (actionData) => api.post('/api/alex/execute-action', actionData),
  getAvailableActions: () => api.get('/api/alex/available-actions'),
  
  // Alertes météo
  getWeatherAlerts: () => api.get('/api/alex/weather-alerts'),
  getWeatherAlertsUnreadCount: () => api.get('/api/alex/weather-alerts/unread-count'),
  checkWeatherAlerts: () => api.post('/api/alex/weather-alerts/check'),
  markAlertRead: (id) => api.put(`/api/alex/weather-alerts/${id}/read`),
  markAllAlertsRead: () => api.put('/api/alex/weather-alerts/read-all'),
  dismissAlert: (id) => api.put(`/api/alex/weather-alerts/${id}/dismiss`),
};

// ============================================
// === MODULE GOLF - API ===
// ============================================

// Employés Golf
export const golfEmployees = {
  list: () => api.get('/api/golf/employees'),
  listArchived: () => api.get('/api/golf/employees?include_archived=true&only_archived=true'),
  create: (data) => api.post('/api/golf/employees', data),
  update: (id, data) => api.put(`/api/golf/employees/${id}`, data),
  delete: (id) => api.delete(`/api/golf/employees/${id}`),
  restore: (id) => api.put(`/api/golf/employees/${id}/restore`),
  checkEmail: (email, excludeId) => api.get('/api/golf/employees/check-email', { params: { email, exclude_employee_id: excludeId } }),
  hourBalanceAudit: (id) => api.get(`/api/golf/employees/${id}/hour-balance-audit`),
};

// Cours Golf (templates récurrents)
export const golfCourses = {
  list: (params) => api.get('/api/golf/courses', { params }),
  create: (data) => api.post('/api/golf/courses', data),
  update: (id, data, regenerate = false) => api.put(`/api/golf/courses/${id}${regenerate ? '?regenerate_sessions=true' : ''}`, data),
  delete: (id) => api.delete(`/api/golf/courses/${id}`),
};

// Sessions de cours (instances générées)
export const golfSessions = {
  list: (params) => api.get('/api/golf/sessions', { params }),
  create: (data) => api.post('/api/golf/sessions', data),
  assign: (sessionId, employeeId) => api.put(`/api/golf/sessions/${sessionId}/assign`, null, { params: { employee_id: employeeId } }),
  update: (sessionId, data) => api.put(`/api/golf/sessions/${sessionId}`, data),
  delete: (sessionId) => api.delete(`/api/golf/sessions/${sessionId}`),
  search: async (q) => { const res = await api.get('/api/golf/sessions/search', { params: { q } }); return res.data; },
};

// Slot Finder (créneaux individuels)
export const slotFinder = {
  findSlots: (data) => api.post('/api/golf/slot-finder/find-slots', data),
  publishSlots: (data) => api.post('/api/golf/slot-finder/publish-slots', data),
  getTemplate: (employeeId) => api.get(`/api/golf/slot-finder/templates/${employeeId}`),
  saveTemplate: (data) => api.post('/api/golf/slot-finder/templates', data),
};

// Clients Golf
export const golfClients = {
  list: (params) => api.get('/api/golf/clients', { params }),
  getFull: (id) => api.get(`/api/golf/clients/${id}/full`),
  groups: () => api.get('/api/golf/clients/groups'),
  createGroup: (data) => api.post('/api/golf/client-types/group', data),
  options: () => api.get('/api/golf/clients/options'),
  create: (data) => api.post('/api/golf/clients', data),
  update: (id, data) => api.put(`/api/golf/clients/${id}`, data),
  delete: (id) => api.delete(`/api/golf/clients/${id}`),
  getSessions: (id) => api.get(`/api/golf/clients/${id}/sessions`),
  getStats: (id) => api.get(`/api/golf/clients/${id}/stats`),
  getDocuments: (id) => api.get(`/api/golf/clients/${id}/documents`),
  import: (formData, listId) => {
    if (listId) formData.append('list_id', listId);
    return api.post('/api/golf/clients/import', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  getDuplicates: () => api.get('/api/golf/clients/duplicates'),
  merge: (targetId, sourceId) => api.post('/api/golf/clients/merge', { target_id: targetId, source_id: sourceId }),
  mergeDuplicatesAuto: (groups) => api.post('/api/golf/clients/duplicates/merge-auto', { groups }),
  ignoreDuplicate: (clientIds) => api.post('/api/golf/clients/duplicates/ignore', { client_ids: clientIds }),
  archive: (id) => api.post(`/api/golf/clients/${id}/archive`),
  unarchive: (id) => api.post(`/api/golf/clients/${id}/unarchive`),
  promoteToEmployee: (id, data) => api.post(`/api/golf/clients/${id}/promote-to-employee`, data),
  revokeEmployeeAccess: (id) => api.post(`/api/golf/clients/${id}/revoke-employee-access`, {}),
  syncEmployeeAccess: (id) => api.post(`/api/golf/clients/${id}/sync-employee-access`, {}),
};

// Listes de clients
export const golfClientLists = {
  list: () => api.get('/api/golf/client-lists'),
  create: (data) => api.post('/api/golf/client-lists', data),
  update: (id, data) => api.put(`/api/golf/client-lists/${id}`, data),
  delete: (id) => api.delete(`/api/golf/client-lists/${id}`),
};

// Congés payés
export const congesApi = {
  dashboard: () => api.get('/api/golf/conges/dashboard'),
  employee: (id) => api.get(`/api/golf/conges/employee/${id}`),
  updateSettings: (id, data) => api.put(`/api/golf/conges/employee/${id}/settings`, data),
  adjust: (id, data) => api.post(`/api/golf/conges/employee/${id}/adjust`, data),
  countDays: (start, end) => api.get(`/api/golf/conges/count-days?start_date=${start}&end_date=${end}`),
  holidays: (year) => api.get(`/api/golf/conges/holidays/${year}`),
};


// Cotisations et groupes
export const golfCotisations = {
  list: () => api.get('/api/golf/cotisations-groups'),
};

// Options de visibilité (grandes catégories)
export const golfVisibilityOptions = {
  list: () => api.get('/api/golf/visibility-options'),
};

// Thèmes de cours (Driving, Putting, etc.)
export const golfCourseThemes = {
  list: () => api.get('/api/golf/course-themes'),
  create: (data) => api.post('/api/golf/course-themes', data),
  update: (id, data) => api.put(`/api/golf/course-themes/${id}`, data),
  delete: (id) => api.delete(`/api/golf/course-themes/${id}`),
};

// Paramètres du golf (logos, infos)
export const golfSettings = {
  get: (includeLogos = false) => api.get('/api/golf/settings', { params: includeLogos ? { include_logos: true } : {} }),
  update: (data) => api.put('/api/golf/settings', data),
  uploadLogo: (logoType, file) => {
    const formData = new FormData();
    formData.append('logo_type', logoType);
    formData.append('file', file);
    return api.post('/api/golf/settings/upload-logo', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  deleteLogo: (logoType) => api.delete(`/api/golf/settings/logo/${logoType}`),
};

// Paramètres du portail client
export const golfPortalSettings = {
  get: () => api.get('/api/golf/portal-settings'),
  update: (data) => api.put('/api/golf/portal-settings', data),
  uploadLogo: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/api/golf/portal-settings/logo', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  deleteLogo: () => api.delete('/api/golf/portal-settings/logo'),
  uploadProLogo: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/api/golf/portal-settings/pro-logo', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  deleteProLogo: () => api.delete('/api/golf/portal-settings/pro-logo'),
};

// Carnet de cours client
export const golfCarnet = {
  update: (clientId, data) => api.put(`/api/golf/clients/${clientId}/carnet`, data),
  debit: (clientId, durationMinutes = 60) => api.post(`/api/golf/clients/${clientId}/carnet/debit?duration_minutes=${durationMinutes}`),
  credit: (clientId, amount = 1) => api.post(`/api/golf/clients/${clientId}/carnet/credit?amount=${amount}`),
};

// Templates de documents (Fitting, Portes ouvertes, etc.)
export const golfDocumentTemplates = {
  list: () => api.get('/api/golf/document-templates'),
  create: (formData) => api.post('/api/golf/document-templates', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  update: (id, formData) => api.put(`/api/golf/document-templates/${id}`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  delete: (id) => api.delete(`/api/golf/document-templates/${id}`),
  download: (id) => api.get(`/api/golf/document-templates/${id}/download`, { responseType: 'blob' }),
};

// Actualités Golf
export const golfNews = {
  list: (publishedOnly = false, archived = false) => api.get(`/api/golf/news?published_only=${publishedOnly}&archived=${archived}`),
  create: (data) => api.post('/api/golf/news', data),
  update: (id, data) => api.put(`/api/golf/news/${id}`, data),
  delete: (id) => api.delete(`/api/golf/news/${id}`),
  archive: (id) => api.post(`/api/golf/news/${id}/archive`),
  unarchive: (id) => api.post(`/api/golf/news/${id}/unarchive`),
  reorder: (newsIds) => api.post('/api/golf/news/reorder', { news_ids: newsIds }),
};

// Remarques sur les sessions
export const golfSessionRemarks = {
  list: (params) => api.get('/api/golf/session-remarks', { params }),
  create: (data) => api.post('/api/golf/session-remarks', data),
  createWithVideo: (formData) => api.post('/api/golf/session-remarks/with-video', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  createWithMedia: (formData) => api.post('/api/golf/session-remarks/with-media', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  // Créer un post indépendant (pas lié à une session) pour le fil
  createPost: (formData) => api.post('/api/golf/session-remarks/post', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  markRead: (id) => api.put(`/api/golf/session-remarks/${id}/read`),
  markAllRead: () => api.put('/api/golf/session-remarks/mark-all-read'),
  getUnreadCount: () => api.get('/api/golf/session-remarks/unread-count'),
  delete: (id) => api.delete(`/api/golf/session-remarks/${id}`),
  // Likes
  toggleLike: (id) => api.post(`/api/golf/session-remarks/${id}/like`),
  // Commentaires
  getComments: (id) => api.get(`/api/golf/session-remarks/${id}/comments`),
  addComment: (id, text) => api.post(`/api/golf/session-remarks/${id}/comments`, { text }),
  // Sauvegardes
  toggleSave: (id) => api.post(`/api/golf/session-remarks/${id}/save`),
  getSaved: () => api.get('/api/golf/session-remarks/saved'),
};

// Conversions Portes Ouvertes
export const golfConversions = {
  getClientHistory: (clientId) => api.get(`/api/golf/clients/${clientId}/session-history`),
  convert: (clientId, data) => api.post(`/api/golf/clients/${clientId}/convert`, data),
  unconvert: (clientId) => api.post(`/api/golf/clients/${clientId}/unconvert`),
  getStats: (params) => api.get('/api/golf/portes-ouvertes/conversion-stats', { params }),
};

// Produits de conversion (paramètres)
export const golfConversionProducts = {
  list: () => api.get('/api/golf/conversion-products'),
  create: (data) => api.post('/api/golf/conversion-products', data),
  update: (id, data) => api.put(`/api/golf/conversion-products/${id}`, data),
  delete: (id) => api.delete(`/api/golf/conversion-products/${id}`),
};

// Gestion des inscriptions (Admin)
export const golfAdminRegistrations = {
  add: (data) => api.post('/api/golf/admin/registrations', data),
  cancel: (registrationId, isPortesOuvertes = false) => 
    api.delete(`/api/golf/admin/registrations/${registrationId}?is_portes_ouvertes=${isPortesOuvertes}`),
};

// Budget et CA Golf
export const golfBudget = {
  list: (params) => api.get('/api/golf/budget', { params }),
  create: (data) => api.post('/api/golf/budget', data),
  update: (id, data) => api.put(`/api/golf/budget/${id}`, data),
  delete: (id) => api.delete(`/api/golf/budget/${id}`),
  importExcel: (formData) => api.post('/api/golf/budget/import-excel', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  importCA: (formData) => api.post('/api/golf/budget/import-ca', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  getCAData: (period) => api.get('/api/golf/budget/ca-data', { params: { period } }),
  getCADataRange: (fromMonth, toMonth) => api.get('/api/golf/budget/ca-data-range', { params: { from_month: fromMonth, to_month: toMonth } }),
  getSynthese: () => api.get('/api/golf/budget/synthese'),
  getSyntheseProrated: () => api.get('/api/golf/budget/synthese-prorated'),
  getCACategories: () => api.get('/api/golf/budget/ca-categories'),
  listEmployeeCAObjectives: () => api.get('/api/golf/budget/employee-ca-objectives'),
  createEmployeeCAObjective: (data) => api.post('/api/golf/budget/employee-ca-objectives', data),
  deleteEmployeeCAObjective: (id) => api.delete(`/api/golf/budget/employee-ca-objectives/${id}`),
  getStatTypes: () => api.get('/api/golf/budget/stat-types'),
  listEmployeeStatObjectives: () => api.get('/api/golf/budget/employee-stat-objectives'),
  createEmployeeStatObjective: (data) => api.post('/api/golf/budget/employee-stat-objectives', data),
  deleteEmployeeStatObjective: (id) => api.delete(`/api/golf/budget/employee-stat-objectives/${id}`),
  getEmployeeStatsLive: () => api.get('/api/golf/budget/employee-stats-live'),
  getEmployeeProfileObjectives: (empId, month) => api.get(`/api/golf/budget/employee-profile-objectives/${empId}`, { params: month ? { month } : {} }),
};

// Objectifs de CA par catégorie
export const golfCAObjectives = {
  list: (params) => api.get('/api/golf/ca-objectives', { params }),
  create: (data) => api.post('/api/golf/ca-objectives', data),
  delete: (id) => api.delete(`/api/golf/ca-objectives/${id}`),
};

// Catégories personnalisées
export const golfCustomCategories = {
  list: (params) => api.get('/api/golf/custom-categories', { params }),
  create: (data) => api.post('/api/golf/custom-categories', data),
  update: (id, data) => api.put(`/api/golf/custom-categories/${id}`, data),
  delete: (id) => api.delete(`/api/golf/custom-categories/${id}`),
};

// Objectifs avancés des pros
export const golfProObjectives = {
  list: (params) => api.get('/api/golf/pro-objectives', { params }),
  create: (data) => api.post('/api/golf/pro-objectives', data),
  update: (id, data) => api.put(`/api/golf/pro-objectives/${id}`, data),
  updateManualValue: (id, value) => api.put(`/api/golf/pro-objectives/${id}/manual-value?value=${value}`),
  delete: (id) => api.delete(`/api/golf/pro-objectives/${id}`),
};

// Objectifs des pros
export const golfObjectives = {
  list: (params) => api.get('/api/golf/objectives', { params }),
  create: (data) => api.post('/api/golf/objectives', data),
  delete: (id) => api.delete(`/api/golf/objectives/${id}`),
};

// Types d'objectifs
export const golfObjectiveTypes = {
  list: () => api.get('/api/golf/objective-types'),
  create: (data) => api.post('/api/golf/objective-types', data),
  update: (id, data) => api.put(`/api/golf/objective-types/${id}`, data),
  delete: (id) => api.delete(`/api/golf/objective-types/${id}`),
  // Sources dynamiques pour les objectifs
  getAvailableSources: () => api.get('/api/golf/objectives/available-sources'),
  testCalculation: (data) => api.post('/api/golf/objectives/test-calculation', data),
};

// Objectifs par employé
export const golfEmployeeObjectives = {
  list: (empId) => api.get(`/api/golf/employees/${empId}/objectives`),
  create: (empId, data) => api.post(`/api/golf/employees/${empId}/objectives`, data),
  update: (objId, data) => api.put(`/api/golf/objectives/${objId}`, data),
  delete: (empId, objId) => api.delete(`/api/golf/objectives/${objId}`),
};

// Budgets annuels des dépenses
export const golfExpenseBudgets = {
  list: (params) => api.get('/api/golf/expense-budgets', { params }),
  create: (data) => api.post('/api/golf/expense-budgets', data),
  delete: (id) => api.delete(`/api/golf/expense-budgets/${id}`),
};

// Paramètres de la synthèse (employés inclus dans les salaires)
export const golfSyntheseSettings = {
  get: (params) => api.get('/api/golf/synthese-settings', { params }),
  update: (data) => api.post('/api/golf/synthese-settings', data),
  getSummary: (params) => api.get('/api/golf/synthese-summary', { params }),
};

// Taylor Made / Fitting stats
export const taylorMadeStats = {
  getStats: (params) => api.get('/api/taylor-made/stats', { params }),
};

// AI Advisor - Conseiller IA
export const aiAdvisor = {
  // Démarre une analyse en arrière-plan, retourne { task_id, status: "running" }
  startAnalyze: (data) => api.post('/api/golf/ai-advisor/analyze', data),
  // Poll le résultat d'une analyse
  pollAnalyze: (taskId) => api.get(`/api/golf/ai-advisor/analyze/${taskId}`),
  getHistory: (limit = 20) => api.get('/api/golf/ai-advisor/history', { params: { limit } }),
  getAnalysis: (historyId) => api.get(`/api/golf/ai-advisor/history/${historyId}`),
  deleteAnalysis: (historyId) => api.delete(`/api/golf/ai-advisor/history/${historyId}`),
};

// Notification Inbox
export const notifInbox = {
  getNotifications: (userId, target) => api.get('/api/push/inbox', { params: { user_id: userId, target } }),
  getUnreadCount: (userId, target) => api.get('/api/push/inbox/unread-count', { params: { user_id: userId, target } }),
  markAllRead: (userId, target) => api.put('/api/push/inbox/read-all', { user_id: userId, target }),
};

// Audit log - Historique des actions
export const auditLog = {
  list: (params = {}) => api.get('/api/admin/audit-log', { params }),
  getCategories: () => api.get('/api/admin/audit-log/categories'),
  getSettings: () => api.get('/api/admin/audit-log/settings'),
  updateSettings: (disabled_categories) => api.put('/api/admin/audit-log/settings', { disabled_categories }),
  delete: (id) => api.delete(`/api/admin/audit-log/${id}`),
};

// Galerie photos/vidéos d'un événement (compétitions/afterworks/ateliers)
export const competitionMedia = {
  uploadPhoto: (competitionId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/api/competitions/${competitionId}/media/photos`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  deletePhoto: (competitionId, photoId) =>
    api.delete(`/api/competitions/${competitionId}/media/photos/${photoId}`),
  addVideo: (competitionId, { title, embed_html }) =>
    api.post(`/api/competitions/${competitionId}/media/videos`, { title, embed_html }),
  deleteVideo: (competitionId, videoId) =>
    api.delete(`/api/competitions/${competitionId}/media/videos/${videoId}`),
};

export default api;
